import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { inr } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { Users, PhoneCall, CheckCircle2, IndianRupee, TrendingUp, TrendingDown, FileText, Percent } from "lucide-react";

const COLORS = ["#3B82F6", "#94A3B8", "#F43F5E", "#F59E0B", "#10B981", "#6366F1", "#A1A1AA", "#0EA5E9"];

function Delta({ v }) {
  if (v == null) return null;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${up ? "text-emerald-600" : "text-rose-600"}`}><Icon className="h-3 w-3" />{Math.abs(v).toFixed(1)}%</span>;
}

export default function Analytics() {
  const { data: sum, isLoading } = useQuery({ queryKey: ["analytics-summary"], queryFn: async () => (await api.get("/analytics/summary")).data });
  const { data: byStatus = [] } = useQuery({ queryKey: ["leads-by-status"], queryFn: async () => (await api.get("/analytics/leads-by-status")).data });

  const t = sum?.totals || {};
  const d = sum?.delta_vs_prev_period || {};
  const cards = [
    { icon: Users, label: "Leads", value: (t.leads ?? 0).toLocaleString("en-IN"), delta: d.leads, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-950/40" },
    { icon: PhoneCall, label: "Total Calls", value: (t.total_calls ?? 0).toLocaleString("en-IN"), delta: d.total_calls, color: "text-violet-600", bg: "bg-violet-50 dark:bg-violet-950/40" },
    { icon: CheckCircle2, label: "Converted", value: t.converted_leads ?? 0, delta: d.converted_leads, color: "text-emerald-600", bg: "bg-emerald-50 dark:bg-emerald-950/40" },
    { icon: FileText, label: "Applications", value: t.applications ?? 0, color: "text-indigo-600", bg: "bg-indigo-50 dark:bg-indigo-950/40" },
    { icon: IndianRupee, label: "Disbursed", value: inr(t.total_disbursed_amount), delta: d.disbursed_amount, color: "text-green-600", bg: "bg-green-50 dark:bg-green-950/40" },
    { icon: Percent, label: "Conversion", value: `${t.conversion_rate_pct ?? 0}%`, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950/40" },
  ];

  return (
    <div data-testid="analytics-page">
      <PageHeader title="Analytics" subtitle={sum ? `Last ${sum.window?.days ?? 30} days` : "Performance overview"} />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {cards.map((c) => (
          <Card key={c.label} className="p-5 border-slate-200/80 dark:border-slate-800/80" data-testid={`analytics-kpi-${c.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{c.label}</div>
                {isLoading ? <Skeleton className="h-8 w-20 mt-2" /> : (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-2xl font-extrabold font-num text-slate-900 dark:text-slate-50">{c.value}</span>
                    <Delta v={c.delta} />
                  </div>
                )}
              </div>
              <div className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon className={`h-5 w-5 ${c.color}`} /></div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-5 border-slate-200/80 dark:border-slate-800/80">
          <h3 className="font-heading font-semibold text-slate-800 dark:text-slate-200 mb-4">Leads by Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byStatus} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <XAxis dataKey="status" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={54} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(148,163,184,0.1)" }} contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>{byStatus.map((e, i) => <Cell key={e.status} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200/80 dark:border-slate-800/80">
          <h3 className="font-heading font-semibold text-slate-800 dark:text-slate-200 mb-4">Lead Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={byStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={2}>
                {byStatus.map((e, i) => <Cell key={e.status} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
